
class PredictiveSearch extends SearchForm {
  constructor() {
    super();
 
    // Cached results
    this.cachedResults = {};
 
    // DOM elements
    this.resultsContainer = this.querySelector("[data-predictive-search]");
    this.filter = this.querySelector("[data-predictive-search-filter]");
    this.productTypeInput = document.getElementById("product_type_input");
 
    // Config
    this.STORAGE_KEY = "m:search-product-type";
    this.defaultProductType = "";
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = "";
 
    this.init();
  }
 
  init() {
    this.addEventListeners();
 
    // Load last filter from local storage
    const savedType =
      window.localStorage.getItem(this.STORAGE_KEY) || this.defaultProductType;
 
    if (this.filter) {
      const hasSavedType = [...this.filter.options].some(
        (opt) => opt.value === savedType
      );
      const selectedType = hasSavedType ? savedType : this.defaultProductType;
 
      this.filter.value = selectedType;
      this.productTypeInput.value = selectedType;
    }
  }
 
  addEventListeners() {
    this.input.form.addEventListener("submit", this.onSubmit.bind(this));
    this.input.addEventListener("focus", this.onFocus.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("keydown", this.onKeyDown.bind(this));
 
    if (this.filter) {
      this.filter.addEventListener("change", this.onFilterChange.bind(this));
    }
  }
 
  getQuery() {
    return this.input.value.trim();
  }
 
  onSubmit(e) {
    const query = this.getQuery();
   
    // If query is empty, prevent default to avoid empty search
    if (!query.length) {
      e.preventDefault();
      return;
    }
   
    // If there's a query and we're submitting via Enter from the input field,
    // we want to go to search page, not to first product
    if (this.input.matches(':focus')) {
      e.preventDefault();
      this.goToSearchPage(query);
    }
    // Otherwise, let the form submit normally (e.g., when clicking search button)
  }
 
  onFilterChange(e) {
    const type = e.target.value;
    this.productTypeInput.value = type;
    window.localStorage.setItem(this.STORAGE_KEY, type);
 
    const query = this.getQuery();
    if (query.length > 0) this.fetchResults(query);
  }
 
  onFocus() {
    const query = this.getQuery();
    if (query.length > 0) this.fetchResults(query);
  }
 
  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }
 
  onKeyUp(e) {
    const query = this.getQuery();
 
    if (!query.length) return this.close(true);
    if (["ArrowUp", "ArrowDown"].includes(e.code)) e.preventDefault();
 
    switch (e.code) {
      case "ArrowUp":
        this.switchOption("up");
        break;
      case "ArrowDown":
        this.switchOption("down");
        break;
      case "Enter":
        // Handle Enter key - always go to search page
        this.handleEnterKey(query);
        break;
      default:
        this.fetchResults(query);
    }
  }
 
  onKeyDown(e) {
    if (["ArrowUp", "ArrowDown"].includes(e.code)) e.preventDefault();
  }
 
  handleEnterKey(query) {
    // If query is empty, let the form submit normally
    if (!query || query.trim().length === 0) {
      return; // Let form submit normally (will go to search page with empty query)
    }
   
    // Prevent form submission to avoid going to first product
    if (this.input.form) {
      this.input.form.addEventListener('submit', (e) => {
        e.preventDefault();
      }, { once: true });
    }
   
    // Always go to search page when Enter is pressed (if there's a query)
    this.goToSearchPage(query);
  }
 
  goToSearchPage(query) {
    const productType = this.productTypeInput?.value || "";
    const form = this.input.form;
    const action = form.getAttribute('action') || '/search';
   
    let url = `${action}?q=${encodeURIComponent(query)}&options[prefix]=last`;
    if (productType) {
      url += `&filter.p.product_type=${encodeURIComponent(productType)}`;
    }
   
    window.location.href = url;
  }
 
async fetchResults(query) {

  // OLD REQUEST CANCEL
  if (this.abortController) {
    this.abortController.abort();
  }

  // NEW CONTROLLER
  this.abortController = new AbortController();

  const productType = this.productTypeInput?.value || "";
  const searchKey = `${productType}-${query}`.toLowerCase();

  this.setLoading(true);

  try {

    const url =
  `/apps/styleflip/api/shopifyproducts/search/${encodeURIComponent(query)}`;

    const response = await fetch(url, {
      signal: this.abortController.signal
    });

    if (!response.ok) {
      throw new Error(response.status);
    }

    const html = await response.text();

    const parsedHTML = new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector("#shopify-section-predictive-search").innerHTML;

    this.cachedResults[searchKey] = parsedHTML;

    // ONLY RENDER LATEST QUERY
    if (query === this.getQuery()) {
      this.renderResults(parsedHTML);
    }

  } catch (err) {

    // Ignore aborted requests
    if (err.name !== "AbortError") {
      console.error("Search Error:", err);
      this.close();
    }

  } finally {
    this.setLoading(false);
  }
}
 
  renderResults(html) {
    this.resultsContainer.innerHTML = html;
    this.open();
  }
 
  setLoading(state) {
    this.classList.toggle("m-spinner-loading", state);
  }
 
  open() {
    const header = document.querySelector(".m-section-header");
    const maxHeight = window.innerHeight - header.getBoundingClientRect().bottom;
    this.resultsContainer.style.setProperty("--search-result-max-height", `${maxHeight}px`);
    this.setAttribute("open", true);
    this.input.setAttribute("aria-expanded", true);
    this.isOpen = true;
  }
 
  close(clear = false) {
    if (clear) this.input.value = "";
    this.removeAttribute("open");
    this.input.setAttribute("aria-expanded", false);
    this.resultsContainer.innerHTML = "";
    this.isOpen = false;
  }
 
  // This method should be defined in your parent SearchForm class
  switchOption(direction) {
    if (!this.isOpen) return;
   
    const options = this.querySelectorAll("[role='option']");
    const current = this.querySelector("[aria-selected='true']");
    let next;
   
    if (direction === "up") {
      next = current ? current.previousElementSibling : options[options.length - 1];
    } else {
      next = current ? current.nextElementSibling : options[0];
    }
   
    if (next) {
      [...options].forEach((opt) => opt.setAttribute("aria-selected", "false"));
      next.setAttribute("aria-selected", "true");
    }
  }
}
 
customElements.define("predictive-search", PredictiveSearch);
 